import styled from "@emotion/styled";
import { LocalDateTime } from "@js-joda/core";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import React from "react";
import { formatPriceUSD } from "../../../format";
import { AddressComponents } from "../../../messages";
import { STRINGS } from "../../../strings";
import { globalize, translate } from "../../../translate";
import { getColor } from "../../../utils/generateColor";
import { CurrencyCircleDollarIcon } from "../../../utils/icones";
import { AppButton } from "../../Generic/AppButton";
import Flex from "../../Generic/Flex";

export function VisitExtraHeader(props: {
  patientInitials: string;
  startTimeLocal: LocalDateTime;
  endTimeLocal: LocalDateTime;
  location: AddressComponents;
  price: number | null;
  visitType: string;
  visitDuration: string | null;
  onGoToVisit: () => void;
}) {
  const {
    patientInitials,
    startTimeLocal,
    endTimeLocal,
    location,
    price,
    visitType,
    visitDuration,
  } = props;

  const [isCollapsed, setCollapsed] = React.useState(true);

  const handleToggleCollapse = () => {
    setCollapsed((prev) => !prev);
  };

  return (
    <Root>
      <Divider />
      <LayoutGroup>
        <AnimatePresence initial={false}>
          {isCollapsed ? (
            <VisitExtraHeaderCollapsed
              patientInitials={patientInitials}
              startTimeLocal={startTimeLocal}
              endTimeLocal={endTimeLocal}
              location={location}
              onExpand={handleToggleCollapse}
              onGoToVisit={props.onGoToVisit}
            />
          ) : (
            <VisitExtraHeaderExpanded
              patientInitials={patientInitials}
              startTimeLocal={startTimeLocal}
              endTimeLocal={endTimeLocal}
              location={location}
              price={price}
              visitType={visitType}
              visitDuration={visitDuration}
              onGoToVisit={props.onGoToVisit}
              onCollapse={handleToggleCollapse}
            />
          )}
        </AnimatePresence>
      </LayoutGroup>
    </Root>
  );
}

function VisitExtraHeaderExpanded(props: {
  patientInitials: string;
  startTimeLocal: LocalDateTime;
  endTimeLocal: LocalDateTime;
  location: AddressComponents;
  price: number | null;
  visitType: string;
  visitDuration: string | null;
  onCollapse: () => void;
  onGoToVisit: () => void;
}) {
  const { patientInitials, startTimeLocal, endTimeLocal, location, price } = props;

  return (
    <motion.div initial={{ height: 56 }} animate={{ height: "auto" }} transition={{ type: "just" }}>
      <Flex direction="column">
        <Header onClick={props.onCollapse}>
          <Avatar>{patientInitials}</Avatar>
          <HeaderContent>
            <ScheduleDate>{formatDate(startTimeLocal, endTimeLocal)}</ScheduleDate>
            <Address>{formatAddress(location)}</Address>
          </HeaderContent>
          {price !== null && (
            <VisitPrice>
              <VisitPriceValue>{formatPriceUSD(price)}/h</VisitPriceValue>
              <VisitPriceIcon as={CurrencyCircleDollarIcon} />
            </VisitPrice>
          )}
        </Header>
        <Footer>
          <HeaderExpandedDetails
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: "just" }}
          >
            <VisitTypeAndDuration>
              <VisitType>{props.visitType}</VisitType>
              {props.visitDuration !== null && (
                <>
                  <span> / </span>
                  <VisitDuration>{props.visitDuration}</VisitDuration>
                </>
              )}
            </VisitTypeAndDuration>
          </HeaderExpandedDetails>

          <motion.div layoutId="button">
            <PulsedButton size="small" onClick={props.onGoToVisit}>
              {translate(STRINGS.VIEW_VISIT)}
            </PulsedButton>
          </motion.div>
        </Footer>
      </Flex>
    </motion.div>
  );
}

function VisitExtraHeaderCollapsed(props: {
  patientInitials: string;
  startTimeLocal: LocalDateTime;
  endTimeLocal: LocalDateTime;
  location: AddressComponents;
  onGoToVisit: () => void;
  onExpand: () => void;
}) {
  const { patientInitials, startTimeLocal, endTimeLocal, location } = props;

  return (
    <motion.div
      initial={{ height: 100 }}
      animate={{ height: "auto" }}
      transition={{ type: "just" }}
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Header onClick={props.onExpand}>
          <Avatar>{patientInitials}</Avatar>
          <HeaderContent>
            <ScheduleDate>{formatDate(startTimeLocal, endTimeLocal)}</ScheduleDate>
            <Address>{formatAddress(location)}</Address>
          </HeaderContent>
        </Header>
        <motion.div layoutId="button" transition={{ type: "just" }}>
          <PulsedButton size="small" onClick={props.onGoToVisit}>
            {translate(STRINGS.VIEW_VISIT)}
          </PulsedButton>
        </motion.div>
      </Flex>
    </motion.div>
  );
}

function formatDate(startTimeLocal: LocalDateTime, endTimeLocal: LocalDateTime) {
  const format = (date: LocalDateTime) =>
    globalize().formatLocalDateTime(
      date,
      startTimeLocal.year() === endTimeLocal.year() ? { skeleton: "MMMd" } : { date: "short" }
    );

  const startTime = format(startTimeLocal);
  const endTime = format(endTimeLocal);

  return startTime === endTime ? startTime : `${startTime} - ${endTime}`;
}

function formatAddress(address: AddressComponents): string {
  return [address.sublocalityLevel1, address.neighborhood, address.locality]
    .filter(Boolean)
    .join(", ");
}

const Root = styled(motion.div)``;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--tw-slate-100);
  margin-bottom: 12px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  gap: 4px;
`;

const Avatar = styled.div<{ children: string }>`
  --size: 48px;
  background-color: ${({ children }) => getColor(children)};
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  width: var(--size);
  height: var(--size);
  border-radius: 20px;
  font-size: 16px;
  font-weight: 700;
  border: 4px solid white;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ScheduleDate = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
`;

const Address = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--tw-slate-500);
`;

const VisitPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-inline-start: auto;
  align-self: flex-start;
  padding: 8px;
`;

const VisitPriceValue = styled.span``;

const VisitPriceIcon = styled.svg`
  font-size: 24px;
  color: var(--tw-slate-400);
`;

const VisitTypeAndDuration = styled.div`
  font-size: 14px;
  line-height: 20px;
`;

const VisitType = styled.span`
  color: var(--tw-slate-500);
  font-weight: 600;
`;

const VisitDuration = styled.strong`
  color: var(--tw-slate-900);
  font-weight: 700;
  text-transform: lowercase;
`;

const PulsedButton = styled(AppButton)`
  animation: pulseButton 2s infinite;
`;

const HeaderExpandedDetails = styled(motion.div)`
  flex: 1;
`;
